
import React, { useState, useEffect } from 'react';
import { Coupon, DiscountType } from '../types';
import { CURRENCIES } from '../constants';

interface CreateCouponModalProps {
  onClose: () => void;
  onSave: (coupon: Coupon) => void;
}

type CodeFormat = 'SHORT' | 'MEDIUM' | 'LONG' | 'CUSTOM';

const CreateCouponModal: React.FC<CreateCouponModalProps> = ({ onClose, onSave }) => {
  const [isManual, setIsManual] = useState(false);
  const [codeLength, setCodeLength] = useState(8);
  const [format, setFormat] = useState<CodeFormat>('MEDIUM');
  
  const [newCoupon, setNewCoupon] = useState<Partial<Coupon>>({
    code: '',
    discountType: DiscountType.PERCENTAGE,
    value: 0,
    currency: 'INR',
    startDate: new Date().toISOString().split('T')[0],
    expiryDate: null,
    minPurchase: null,
    limitPerCustomer: true,
    maxRedemptions: null,
    status: 'Active'
  });

  const generateCode = (length: number) => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < length; i++) result += chars.charAt(Math.floor(Math.random() * chars.length));
    setNewCoupon(prev => ({ ...prev, code: result }));
  };

  const handleFormatChange = (f: CodeFormat) => {
    setFormat(f);
    let len = 8;
    if (f === 'SHORT') len = 6;
    if (f === 'MEDIUM') len = 10;
    if (f === 'LONG') len = 16;
    
    if (f !== 'CUSTOM') {
      setCodeLength(len);
      if (!isManual) generateCode(len);
    }
  };

  useEffect(() => {
    if (!isManual && newCoupon.code === '') {
      generateCode(codeLength);
    }
  }, [isManual, codeLength]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const today = new Date().toISOString().split('T')[0];
    const status = (newCoupon.startDate || today) > today ? 'Scheduled' : 'Active';
    
    const couponToAdd: Coupon = {
      ...newCoupon as Coupon,
      id: Date.now().toString(),
      currentRedemptions: 0,
      status: status,
      currency: newCoupon.currency || 'INR'
    };
    onSave(couponToAdd);
  };

  const selectedCurrencySymbol = CURRENCIES.find(c => c.code === newCoupon.currency)?.symbol || '₹';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md" onClick={onClose}></div>
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-white sticky top-0 z-10">
          <div>
            <h2 className="text-xl font-black text-slate-900">Create Discount Code</h2>
            <p className="text-slate-500 text-xs">Configure your promotion with Shopify-style precision.</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-8 space-y-10">
          
          {/* Section: Code Generation */}
          <section className="space-y-4">
            <div className="flex justify-between items-end">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Discount Code</label>
              <div className="flex bg-slate-100 p-1 rounded-lg">
                <button 
                  type="button" 
                  onClick={() => setIsManual(false)}
                  className={`px-3 py-1 text-[10px] font-black rounded-md transition-all ${!isManual ? 'bg-white shadow-sm text-indigo-600' : 'text-slate-500 hover:text-slate-700'}`}
                >Automated</button>
                <button 
                  type="button" 
                  onClick={() => setIsManual(true)}
                  className={`px-3 py-1 text-[10px] font-black rounded-md transition-all ${isManual ? 'bg-white shadow-sm text-indigo-600' : 'text-slate-500 hover:text-slate-700'}`}
                >Manual</button>
              </div>
            </div>

            {isManual ? (
              <input 
                required 
                className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-xl font-bold uppercase tracking-tight" 
                value={newCoupon.code} 
                placeholder="e.g. FLASH50" 
                onChange={e => setNewCoupon({...newCoupon, code: e.target.value.toUpperCase()})} 
              />
            ) : (
              <div className="space-y-4 bg-slate-50 p-6 rounded-2xl border border-slate-200">
                <div className="flex gap-4">
                  <div className="flex-1 text-2xl font-mono font-black text-indigo-600 tracking-wider">
                    {newCoupon.code}
                  </div>
                  <button 
                    type="button" 
                    onClick={() => generateCode(codeLength)}
                    className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 2v6h-6"/><path d="M3 12a9 9 0 0 1 15-6.7L21 8"/><path d="M3 22v-6h6"/><path d="M21 12a9 9 0 0 1-15 6.7L3 16"/></svg>
                  </button>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {(['SHORT', 'MEDIUM', 'LONG', 'CUSTOM'] as CodeFormat[]).map(f => (
                    <button
                      key={f}
                      type="button"
                      onClick={() => handleFormatChange(f)}
                      className={`py-2 rounded-xl text-[10px] font-black uppercase transition-all border ${
                        format === f 
                          ? 'bg-indigo-600 text-white border-indigo-600' 
                          : 'bg-white text-slate-500 border-slate-200 hover:border-indigo-200'
                      }`}
                    >{f}</button>
                  ))}
                </div>
                {format === 'CUSTOM' && (
                  <div className="pt-2 flex items-center gap-4">
                    <input 
                      type="range" min="4" max="24" value={codeLength} 
                      onChange={e => {
                        const val = parseInt(e.target.value);
                        setCodeLength(val);
                        generateCode(val);
                      }}
                      className="flex-1 accent-indigo-600"
                    />
                    <span className="text-xs font-black text-slate-600 w-12 text-center">{codeLength} chars</span>
                  </div>
                )}
              </div>
            )}
          </section>

          {/* Section: Discount Value */}
          <section className="space-y-6">
             <div className="flex justify-between items-end">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">Value & Type</label>
                <div className="w-32">
                   <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">Currency</label>
                   <select 
                      className="w-full px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg outline-none focus:ring-1 focus:ring-indigo-500 font-bold text-xs"
                      value={newCoupon.currency}
                      onChange={e => setNewCoupon({...newCoupon, currency: e.target.value})}
                   >
                      {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}
                   </select>
                </div>
             </div>
             <div className="grid grid-cols-2 gap-4">
                <div 
                  onClick={() => setNewCoupon({...newCoupon, discountType: DiscountType.PERCENTAGE})}
                  className={`p-4 rounded-2xl border-2 cursor-pointer transition-all ${newCoupon.discountType === DiscountType.PERCENTAGE ? 'border-indigo-600 bg-indigo-50' : 'border-slate-100 hover:border-slate-200 bg-white'}`}
                >
                  <p className="text-xs font-black text-slate-900">Percentage</p>
                  <p className="text-[10px] text-slate-500 mt-1">Ideal for sales events.</p>
                </div>
                <div 
                  onClick={() => setNewCoupon({...newCoupon, discountType: DiscountType.FIXED})}
                  className={`p-4 rounded-2xl border-2 cursor-pointer transition-all ${newCoupon.discountType === DiscountType.FIXED ? 'border-indigo-600 bg-indigo-50' : 'border-slate-100 hover:border-slate-200 bg-white'}`}
                >
                  <p className="text-xs font-black text-slate-900">Fixed Amount</p>
                  <p className="text-[10px] text-slate-500 mt-1">Ideal for gift vouchers.</p>
                </div>
             </div>
             <div className="relative">
                <span className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 font-black">
                  {newCoupon.discountType === DiscountType.PERCENTAGE ? '%' : selectedCurrencySymbol}
                </span>
                <input 
                  required type="number" 
                  className="w-full pl-10 pr-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 font-bold text-xl" 
                  value={newCoupon.value || ''} 
                  placeholder="0"
                  onChange={e => setNewCoupon({...newCoupon, value: parseInt(e.target.value) || 0})} 
                />
             </div>
          </section>

          {/* Section: Requirements & Eligibility */}
          <section className="space-y-6">
            <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest border-b border-slate-100 pb-2">Conditions</h3>
            
            <div className="space-y-4">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">Minimum Requirements</label>
              <div className="relative">
                <span className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 font-black">{selectedCurrencySymbol}</span>
                <input 
                  type="number" 
                  className="w-full pl-10 pr-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 text-sm font-bold" 
                  placeholder="Minimum purchase amount (Optional)"
                  value={newCoupon.minPurchase || ''}
                  onChange={e => setNewCoupon({...newCoupon, minPurchase: e.target.value ? parseInt(e.target.value) : null})}
                />
              </div>
            </div>

            <div className="flex items-center justify-between p-5 bg-slate-50 rounded-2xl border border-slate-100">
              <div className="space-y-0.5">
                <p className="text-sm font-bold text-slate-900">One use per customer</p>
                <p className="text-[10px] text-slate-500">Limits redemption to once per email address.</p>
              </div>
              <button 
                type="button" 
                onClick={() => setNewCoupon({...newCoupon, limitPerCustomer: !newCoupon.limitPerCustomer})}
                className={`w-12 h-6 rounded-full relative p-1 transition-all duration-300 ${newCoupon.limitPerCustomer ? 'bg-indigo-600' : 'bg-slate-300'}`}
              >
                <div className={`w-4 h-4 bg-white rounded-full shadow-md transition-transform duration-300 transform ${newCoupon.limitPerCustomer ? 'translate-x-6' : 'translate-x-0'}`}></div>
              </button>
            </div>
          </section>

          {/* Section: Active Dates */}
          <section className="space-y-6">
            <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest border-b border-slate-100 pb-2">Active Dates</h3>
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Start Date</label>
                <input required type="date" className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-sm outline-none focus:ring-2 focus:ring-indigo-500" value={newCoupon.startDate} onChange={e => setNewCoupon({...newCoupon, startDate: e.target.value})} />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">End Date (Optional)</label>
                <input type="date" className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-sm outline-none focus:ring-2 focus:ring-indigo-500" value={newCoupon.expiryDate || ''} onChange={e => setNewCoupon({...newCoupon, expiryDate: e.target.value || null})} />
              </div>
            </div>
          </section>

        </form>

        {/* Footer */}
        <div className="p-6 bg-slate-50 border-t border-slate-100 flex items-center justify-between sticky bottom-0 z-10">
          <div className="hidden md:block">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Summary</p>
            <p className="text-xs font-bold text-slate-600">
              {newCoupon.discountType === DiscountType.PERCENTAGE ? `${newCoupon.value}%` : `${selectedCurrencySymbol}${newCoupon.value}`} off {newCoupon.minPurchase ? `on orders over ${selectedCurrencySymbol}${newCoupon.minPurchase}` : 'all plans'}
            </p>
          </div>
          <div className="flex gap-3 w-full md:w-auto">
            <button type="button" onClick={onClose} className="flex-1 md:flex-none px-6 py-3 text-xs font-black text-slate-500 uppercase tracking-widest hover:text-slate-700 transition-colors">Cancel</button>
            <button 
              type="button"
              onClick={handleSubmit}
              className="flex-1 md:flex-none px-8 py-3 bg-indigo-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-indigo-700 shadow-xl shadow-indigo-100 transition-all active:scale-95"
            >
              Launch Code
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateCouponModal;
